import type { Annotation } from '../schemas/inspectionReport';

// ============================================================================
// Annotation render constants + geometry, shared by the editor overlay
// (AnnotationLayer) and the PDF generator. Both draw into the same
// `viewBox="0 0 100 100" preserveAspectRatio="none"` overlay, so every size here
// is in viewBox units (1 unit = 1% of the page box) — that is what keeps the two
// renderers identical (the parity contract, see the design spec).
// ============================================================================

// ── Colors ────────────────────────────────────────────────────────────────
// Annotations carry a color key, not a hex: the key is what gets stored on the
// annotation, so the palette's actual hexes stay tunable without rewriting saved
// documents. Chosen for contrast against real inspection photos — red reads on
// most mid-tone scenes but disappears on rust/wood/shadow, yellow carries dark
// backgrounds, black is the only reliable option on blown-out/white ones, and
// cyan covers warm beige/concrete scenes (and is the hue least likely to occur
// naturally in a construction photo). None of the four collide for red-green
// colorblind viewers.

export const ANNO_COLORS = [
  { key: 'red', label: 'Red', hex: '#e11d2a' },
  { key: 'yellow', label: 'Yellow', hex: '#ffcc00' },
  { key: 'cyan', label: 'Cyan', hex: '#00b8d4' },
  { key: 'black', label: 'Black', hex: '#000000' },
] as const;

export type AnnoColor = (typeof ANNO_COLORS)[number]['key'];

/** The palette's keys, as the non-empty tuple `z.enum` wants. Derived from
 *  ANNO_COLORS so the schema and the renderers can never drift apart. */
export const ANNO_COLOR_KEYS = ANNO_COLORS.map((c) => c.key) as [AnnoColor, ...AnnoColor[]];

/** Color an annotation renders in when it stores none. Matches the `.ins-anno` CSS. */
export const DEFAULT_ANNO_COLOR: AnnoColor = 'red';

/** Stored color key → the hex both renderers paint with. Unknown keys (an older
 *  client, a hand-edited document) fall back to the default rather than throwing. */
export function annoColorHex(color?: string): string {
  return ANNO_COLORS.find((c) => c.key === color)?.hex ?? ANNO_COLORS[0].hex;
}

/** Text size when an annotation stores none. Matches the `.ins-anno-text` CSS. */
export const DEFAULT_ANNO_TEXT_SIZE = 3.5;

/** Arrow head size when an arrow stores none. Matches the original marker box. */
export const DEFAULT_ARROW_HEAD_SIZE = 4;

export const MIN_ARROW_HEAD_SIZE = 1;
export const MAX_ARROW_HEAD_SIZE = 12;

// Rendered height of the page box in points: the PDF's `.ins-page` min-height
// (10.2in). The overlay's vertical axis spans that box, so one viewBox unit of
// font-size renders as 1% of it — which is how the inspector's point sizes are
// converted. The editor canvas draws the page a little taller (a full 11in), so
// point sizes there run ~8% large; that offset is inherent to the existing page
// box difference, not to this conversion.
export const ANNO_PAGE_BOX_PT = 10.2 * 72;

/** viewBox units → the point size the text renders at in the PDF. */
export const annoUnitsToPt = (units: number): number => (units * ANNO_PAGE_BOX_PT) / 100;

/** Point size → viewBox units, rounded to keep the stored JSON tidy. */
export const annoPtToUnits = (pt: number): number =>
  Math.round(((pt * 100) / ANNO_PAGE_BOX_PT) * 10000) / 10000;

/** Point sizes offered in the annotation text-size menu (mirrors the rich-text one). */
export const ANNO_TEXT_PT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72];

/** Stroke width (px) of `.ins-anno` — the width every annotation is drawn at
 *  unless it scales its own, and the width the DEFAULT_ARROW_HEAD_SIZE head was
 *  drawn against. */
export const ANNO_STROKE_PX = 2;

/**
 * The stroke weight a sized segment draws at, in proportion to the default head
 * size — so a dimension's size scales its whole shape (line, tick caps and the
 * two strokes of each head), and a plain line's size, having no head to grow,
 * scales its thickness alone. In px because `.ins-anno` uses
 * `vector-effect: non-scaling-stroke` — the width is screen px in both renderers.
 */
export const annoStrokePx = (size?: number): number => {
  const s = clampArrowHeadSize(size ?? DEFAULT_ARROW_HEAD_SIZE);
  return Math.round((ANNO_STROKE_PX * s) / DEFAULT_ARROW_HEAD_SIZE * 100) / 100;
};

export const clampArrowHeadSize = (size: number): number => {
  const clamped = Math.max(MIN_ARROW_HEAD_SIZE, Math.min(MAX_ARROW_HEAD_SIZE, size));
  return Math.round(clamped * 100) / 100;
};

export interface ArrowMarker {
  /** Marker element id, derived from the kind, color AND size so one id always
   *  means the same head — SVG ids repeat across pages/overlays in one document
   *  and a `url(#id)` reference resolves to the first match in document order.
   *  Color is part of the id because the head is painted, not inherited: two
   *  heads of the same size in different colors need two markers. */
  id: string;
  size: number;
  refX: number;
  refY: number;
  path: string;
  /** Hex the head paints with, so it matches the line it terminates. A filled
   *  head uses it as its fill, an open one as its stroke. */
  color: string;
  /** A solid triangle (the arrow's head) vs. an open two-stroke chevron (the
   *  dimension's ends). Renderers fill the first and stroke the second. */
  filled: boolean;
}

/** Geometry of the arrow head marker for a given (optional) stored size + color. */
export function arrowMarker(size?: number, color?: string): ArrowMarker {
  const s = clampArrowHeadSize(size ?? DEFAULT_ARROW_HEAD_SIZE);
  const c = ANNO_COLORS.find((x) => x.key === color) ?? ANNO_COLORS[0];
  return {
    id: `ins-arrow-${c.key}-${String(s).replace('.', '_')}`,
    size: s,
    // Tip pulled just inside the marker box (same 7/8 ratio as the original
    // 4-unit head), so the head's point lands on the line's end point.
    refX: (s * 7) / 8,
    refY: s / 2,
    path: `M0,0 L${s},${s / 2} L0,${s} Z`,
    color: c.hex,
    filled: true,
  };
}

/**
 * The head on a dimension's ends: the same triangle drawn as two open strokes
 * instead of a solid shape. Its tip sits exactly ON the end point (refX = size,
 * no overshoot), so the point meets the tick cap cleanly at every size — the
 * arrow's head deliberately overshoots by an eighth, which shows as the point
 * bleeding past the cap once the head grows.
 */
export function dimensionMarker(size?: number, color?: string): ArrowMarker {
  const s = clampArrowHeadSize(size ?? DEFAULT_ARROW_HEAD_SIZE);
  const c = ANNO_COLORS.find((x) => x.key === color) ?? ANNO_COLORS[0];
  return {
    id: `ins-dim-${c.key}-${String(s).replace('.', '_')}`,
    size: s,
    refX: s,
    refY: s / 2,
    path: `M0,0 L${s},${s / 2} L0,${s}`,
    color: c.hex,
    filled: false,
  };
}

/** The distinct end markers an overlay needs in its <defs> — one per kind, color
 *  and size in use. Arrows and dimensions wear different heads, hence different
 *  ids. */
export function annotationMarkersFor(annotations: Annotation[]): ArrowMarker[] {
  const byId = new Map<string, ArrowMarker>();
  for (const a of annotations) {
    const m =
      a.type === 'arrow'
        ? arrowMarker(a.headSize, a.color)
        : a.type === 'dimension'
          ? dimensionMarker(a.headSize, a.color)
          : null;
    if (m && !byId.has(m.id)) byId.set(m.id, m);
  }
  return [...byId.values()];
}

/** Tick length relative to the head size — a little longer than the head is
 *  tall, so the caps read as end stops rather than as part of the arrow. */
export const DIMENSION_TICK_RATIO = 1.5;

/** A straight segment in viewBox (0..100) coordinates. */
export interface AnnotationSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const round4 = (v: number): number => Math.round(v * 10000) / 10000;

/**
 * The two perpendicular cap segments of a dimension line, in viewBox units, so
 * the editor overlay and the PDF draw the identical thing from the identical
 * math (the parity contract). Perpendicularity is measured in viewBox space,
 * which is the space both renderers stretch — same as the arrow head markers.
 * A zero-length line has no direction to be perpendicular to, so it gets none.
 */
export function dimensionTicks(a: { x1: number; y1: number; x2: number; y2: number; headSize?: number | undefined }): AnnotationSegment[] {
  const x1 = a.x1 * 100;
  const y1 = a.y1 * 100;
  const x2 = a.x2 * 100;
  const y2 = a.y2 * 100;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return [];
  const half = (clampArrowHeadSize(a.headSize ?? DEFAULT_ARROW_HEAD_SIZE) * DIMENSION_TICK_RATIO) / 2;
  // Unit normal of the line, scaled to half a tick each way from the end point.
  const px = (-dy / len) * half;
  const py = (dx / len) * half;
  return [
    { x1: round4(x1 - px), y1: round4(y1 - py), x2: round4(x1 + px), y2: round4(y1 + py) },
    { x1: round4(x2 - px), y1: round4(y2 - py), x2: round4(x2 + px), y2: round4(y2 + py) },
  ];
}

// ── Rotation ──────────────────────────────────────────────────────────────
// Box-drawn shapes (circle/rect) can carry a tilt, in degrees clockwise about
// their own center. The angle lives in viewBox space, which both renderers
// stretch by the same slot aspect, so the editor overlay and the PDF apply the
// identical transform — the parity contract again. (A consequence: on a
// non-square slot the on-screen tilt is not literal screen degrees. It is what
// the user drags to, and it renders the same in both places, which is what
// matters.)

/** Snap increment offered while rotating with Shift held — 15° so the common
 *  stops (45°, 90°) land exactly rather than being eyeballed. */
export const ROTATION_SNAP_DEGREES = 15;

/** Normalize any angle to (-180, 180], so a stored value is always the shortest
 *  way round and repeated drags can't accumulate turns. */
export function normalizeRotation(deg: number): number {
  const wrapped = ((((deg + 180) % 360) + 360) % 360) - 180;
  // -180 and 180 are the same angle; prefer the positive one so the range is
  // half-open and the two renderers never print different strings for it.
  return round4(wrapped === -180 ? 180 : wrapped);
}

/** Center of a box-drawn annotation in viewBox (0..100) units. */
export function annoCenter(a: Annotation): { cx: number; cy: number } | null {
  if (a.type === 'circle') return { cx: a.x * 100, cy: a.y * 100 };
  if (a.type === 'rect') return { cx: (a.x + a.w / 2) * 100, cy: (a.y + a.h / 2) * 100 };
  return null;
}

/**
 * The SVG `transform` a rotated annotation renders with, or undefined when it
 * has none — an un-tilted shape emits no transform attribute at all, so every
 * document saved before this existed renders byte-identically.
 */
export function annoTransform(a: Annotation): string | undefined {
  if (a.type !== 'circle' && a.type !== 'rect') return undefined;
  const deg = normalizeRotation(a.rotation ?? 0);
  if (deg === 0) return undefined;
  const c = annoCenter(a);
  if (!c) return undefined;
  return `rotate(${deg} ${round4(c.cx)} ${round4(c.cy)})`;
}
